import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledButton from '../../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import {
  startTransition,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import clsx from 'clsx';
import StyledTooltipContainer from '../../styled/StyledTooltipContainer';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import DuplicateIcon from '@/assets/icons/copy-03.svg?react';
import CreateCarRentalCategoryForm from './CreateCarRentalCategoryForm';
import { WEEKDAYS } from '@/constants/weekdays';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';

interface CarRentalCategoriesListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  categoryErrorIndex?: number | null;
  isViewMode?: boolean;
  language: SupportedLocale;
}

const CarRentalCategoriesList = ({
  control,
  categoryErrorIndex,
  isViewMode = false,
  language,
}: CarRentalCategoriesListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'carCategories',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'carCategories',
    });

  const { watch } = useFormContext();
  const carCategories = useWatch({ control, name: 'carCategories' });
  const translations = useWatch({ control, name: 'translations' });
  const commonInsurances = watch('insurances');
  const commonOptions = watch('options');
  const commonTaxes = watch('taxes');
  const commonOneWayFees = watch('oneWayFees');
  const shouldLimitInsurance = watch('shouldLimitInsurance');

  const [userSelectedIndex, setUserSelectedIndex] = useState(0);

  const hasNavigatedToErrorRef = useRef(false);
  const prevErrorIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const currentErrorIndex = categoryErrorIndex;
    const isNewError =
      currentErrorIndex !== null &&
      currentErrorIndex !== prevErrorIndexRef.current &&
      !hasNavigatedToErrorRef.current;

    if (isNewError && currentErrorIndex !== undefined) {
      startTransition(() => {
        setUserSelectedIndex(currentErrorIndex);
      });
      hasNavigatedToErrorRef.current = true;
    }

    if (currentErrorIndex !== prevErrorIndexRef.current) {
      if (currentErrorIndex === null) {
        hasNavigatedToErrorRef.current = false;
      }
      prevErrorIndexRef.current = currentErrorIndex ?? null;
    }
  }, [categoryErrorIndex]);

  const selectedCategoryIndex = userSelectedIndex;

  const getCategoryTitle = useCallback(
    (index: number) => {
      const translation = translations?.[language]?.carCategories?.[index];
      const categoryTranslated =
        translation?.category ?? carCategories?.[index]?.category;
      const modelTranslated =
        translation?.model ?? carCategories?.[index]?.model;
      if (
        carCategories &&
        carCategories.length > index &&
        categoryTranslated &&
        modelTranslated
      )
        return `${categoryTranslated} - ${modelTranslated}`;
      return t('carRental.categoryForm.infoTitle', { index: index + 1 });
    },
    [carCategories, language, t, translations],
  );

  return (
    <div className="flex flex-col md:flex-row gap-6 p-6">
      <div className="md:sticky md:top-20 z-10 md:w-1/4 flex flex-col h-fit">
        {fields.map((item, index) => (
          <div
            key={item.id}
            className={clsx('flex justify-between pr-4 border rounded-md', {
              'bg-brand-50 border-brand-600': index === selectedCategoryIndex,
            })}
          >
            <div
              className="w-full h-full pl-4 py-3 hover:cursor-pointer"
              onClick={() => {
                setUserSelectedIndex(index);
                hasNavigatedToErrorRef.current = false;
              }}
            >
              <h2 className="text-base font-medium text-black w-full h-full">
                {getCategoryTitle(index)}
              </h2>
            </div>
            {!isViewMode && (
              <div className="flex gap-2 w-fit">
                <StyledTooltipContainer
                  tooltipMessage={t('carRental.categoryForm.duplicate')}
                >
                  <DuplicateIcon
                    className="size-5 stroke-gray-500 hover:cursor-pointer"
                    onClick={() => {
                      append({
                        category:
                          carCategories && carCategories.length
                            ? carCategories[index].category
                            : undefined,
                        model:
                          carCategories && carCategories.length
                            ? carCategories[index].model
                            : undefined,
                        description:
                          carCategories && carCategories.length
                            ? carCategories[index].description
                            : undefined,
                        images:
                          carCategories && carCategories.length
                            ? carCategories[index].images
                            : [],
                        maxCapacity:
                          carCategories && carCategories.length
                            ? carCategories[index].maxCapacity
                            : undefined,
                        maxNbLuggage:
                          carCategories && carCategories.length
                            ? carCategories[index].maxNbLuggage
                            : undefined,
                        gearboxType:
                          carCategories && carCategories.length
                            ? carCategories[index].gearboxType
                            : undefined,
                        airConditioning:
                          carCategories && carCategories.length
                            ? carCategories[index].airConditioning
                            : undefined,
                        includedMileage:
                          carCategories && carCategories.length
                            ? carCategories[index].includedMileage
                            : undefined,
                        isIncludedMileageUnlimited:
                          carCategories && carCategories.length
                            ? carCategories[index].isIncludedMileageUnlimited
                            : false,
                        additionalCost:
                          carCategories && carCategories.length
                            ? carCategories[index].additionalCost
                            : undefined,
                        standardDeductableAmount:
                          carCategories && carCategories.length
                            ? carCategories[index].standardDeductableAmount
                            : undefined,
                        depositAmount:
                          carCategories && carCategories.length
                            ? carCategories[index].depositAmount
                            : undefined,
                        minimumAge:
                          carCategories && carCategories.length
                            ? carCategories[index].minimumAge
                            : undefined,
                        included:
                          carCategories && carCategories.length
                            ? carCategories[index].included
                            : undefined,
                        notIncluded:
                          carCategories && carCategories.length
                            ? carCategories[index].notIncluded
                            : undefined,
                        displayCategoryOptions:
                          carCategories && carCategories.length
                            ? carCategories[index].displayCategoryOptions
                            : undefined,
                        tarifs:
                          carCategories && carCategories.length
                            ? carCategories[index].tarifs?.map((tarif) => ({
                                name: tarif.name ?? undefined,
                                periods:
                                  tarif.periods && tarif.periods.length
                                    ? tarif.periods.map((period) => ({
                                        startDate: period.startDate
                                          ? new Date(period.startDate)
                                          : undefined,
                                        endDate: period.endDate
                                          ? new Date(period.endDate)
                                          : undefined,
                                        applicationDays:
                                          period.applicationDays ?? WEEKDAYS,
                                      }))
                                    : [
                                        {
                                          startDate: undefined,
                                          endDate: undefined,
                                          applicationDays: WEEKDAYS,
                                        },
                                      ],
                                pricePartitions:
                                  tarif.pricePartitions &&
                                  tarif.pricePartitions.length
                                    ? tarif.pricePartitions.map(
                                        (partition) => ({
                                          price: partition.price
                                            ? Number(partition.price)
                                            : undefined,
                                          from: partition.from
                                            ? Number(partition.from)
                                            : undefined,
                                          to: partition.to
                                            ? Number(partition.to)
                                            : undefined,
                                        }),
                                      )
                                    : [
                                        {
                                          price: undefined,
                                          from: undefined,
                                          to: undefined,
                                        },
                                      ],
                              }))
                            : [],
                        options:
                          carCategories && carCategories.length
                            ? carCategories[index].options
                            : [],
                        taxes:
                          carCategories && carCategories.length
                            ? carCategories[index].taxes
                            : [],
                        insurances:
                          carCategories && carCategories.length
                            ? carCategories[index].insurances
                            : [],
                        oneWayFees:
                          carCategories && carCategories.length
                            ? carCategories[index].oneWayFees
                            : [],
                        shouldLimitInsurance:
                          carCategories && carCategories.length
                            ? carCategories[index].shouldLimitInsurance
                            : 'false',
                      } as CarRentalFormDataWithTranslations['carCategories'][number]);

                      const categoryTranslations = (lang: SupportedLocale) =>
                        translations?.[lang]?.carCategories?.[index];

                      appendTranslation((lang) => ({
                        category:
                          categoryTranslations(lang)?.category ??
                          carCategories?.[index]?.category ??
                          undefined,
                        model:
                          categoryTranslations(lang)?.model ??
                          carCategories?.[index]?.model ??
                          undefined,
                        description:
                          categoryTranslations(lang)?.description ??
                          carCategories?.[index]?.description ??
                          undefined,
                        included:
                          categoryTranslations(lang)?.included ??
                          carCategories?.[index]?.included ??
                          undefined,
                        notIncluded:
                          categoryTranslations(lang)?.notIncluded ??
                          carCategories?.[index]?.notIncluded ??
                          undefined,
                        insurances:
                          categoryTranslations(lang)?.insurances ??
                          carCategories?.[index]?.insurances ??
                          [],
                        taxes:
                          categoryTranslations(lang)?.taxes ??
                          carCategories?.[index]?.taxes ??
                          [],
                        options:
                          categoryTranslations(lang)?.options ??
                          carCategories?.[index]?.options ??
                          [],
                        oneWayFees:
                          categoryTranslations(lang)?.oneWayFees ??
                          carCategories?.[index]?.oneWayFees ??
                          [],
                      }));
                      setUserSelectedIndex(fields.length);
                      hasNavigatedToErrorRef.current = false;
                    }}
                  />
                </StyledTooltipContainer>
                {fields.length > 1 && (
                  <StyledTooltipContainer
                    tooltipMessage={t('carRental.categoryForm.delete')}
                  >
                    <TrashIcon
                      className="size-5 stroke-gray-500 hover:cursor-pointer"
                      onClick={() => {
                        remove(index);
                        removeTranslation(index);
                        if (userSelectedIndex >= index) {
                          setUserSelectedIndex((current) => {
                            hasNavigatedToErrorRef.current = false;
                            return Math.max(current - 1, 0);
                          });
                        }
                      }}
                    />
                  </StyledTooltipContainer>
                )}
              </div>
            )}
          </div>
        ))}
        {!isViewMode && (
          <StyledButton
            className="text-brand-600 w-fit pl-2 pt-3"
            title={t('carRental.addCarCategory')}
            onClick={() => {
              append({
                category: undefined,
                model: undefined,
                description: undefined,
                images: [],
                maxCapacity: undefined,
                maxNbLuggage: undefined,
                gearboxType: undefined,
                airConditioning: undefined,
                includedMileage: undefined,
                isIncludedMileageUnlimited: undefined,
                additionalCost: undefined,
                standardDeductableAmount: undefined,
                depositAmount: undefined,
                minimumAge: undefined,
                included: undefined,
                notIncluded: undefined,
                displayCategoryOptions: 'true',
                tarifs: [
                  {
                    name: undefined,
                    periods: [
                      {
                        startDate: undefined,
                        endDate: undefined,
                        applicationDays: WEEKDAYS,
                      },
                    ],
                    pricePartitions: [
                      {
                        price: undefined,
                        from: 1,
                        to: 1,
                      },
                    ],
                  },
                ],
                options: commonOptions ?? [],
                taxes: commonTaxes ?? [],
                insurances: commonInsurances ?? [],
                oneWayFees: commonOneWayFees ?? [],
                shouldLimitInsurance: shouldLimitInsurance ?? 'false',
              } as unknown as CarRentalFormDataWithTranslations['carCategories'][number]);
              appendTranslation(() => ({
                category: undefined,
                model: undefined,
                description: undefined,
                included: undefined,
                notIncluded: undefined,
                insurances: commonInsurances ?? [],
                taxes: commonTaxes ?? [],
                options: commonOptions ?? [],
                oneWayFees: commonOneWayFees ?? [],
              }));
              setUserSelectedIndex(fields.length);
            }}
            variant="link"
            icon={<PlusIcon className="size-5 fill-brand-600" />}
          />
        )}
      </div>
      <div className="flex flex-col gap-4 w-full lg:w-1/2 2xl:w-1/3">
        {selectedCategoryIndex >= 0 && (
          <CreateCarRentalCategoryForm
            control={control}
            language={language}
            index={selectedCategoryIndex}
            isViewMode={isViewMode}
            key={`car-category-${selectedCategoryIndex}`}
          />
        )}
      </div>
    </div>
  );
};

export default CarRentalCategoriesList;
